// Phase T2.0A Prompt 3 — Admin Registration Control Center.
//
// One polished admin surface for managing the complete registration state
// of a Tournament OR Round Robin. Reuses:
//   • capacity.js helpers (unique-profile counting, teams = max_entries)
//   • alternatesAPI + paymentsAPI (Prompt 2.2)
//   • tournamentAPI / roundRobinAPI (existing engines)
//   • ConfirmDialog (Prompt 2.2)
//   • MemberLink
//
// Sections (top-down, mobile-first):
//   1. Capacity summary (Teams / Players / Spots)
//   2. Registered players — one row per PROFILE (never per entry) in true
//      individual registration order, with partner label + paid toggle +
//      overflow menu (Change Partner / Set Looking / Set Pending /
//      Separate Team / Remove Player).
//   3. Alternates (AlternatesManager reused verbatim).
//   4. Add Player button → AdminAddPlayerDialog.
//
// The eventKind prop selects which API family we drive; everything else is
// symmetric.

import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, DotsThreeVertical, Users, Check, Trash, ArrowsClockwise, ArrowLeft, MagnifyingGlass, X } from '@phosphor-icons/react';
import MemberLink from '../MemberLink';
import ConfirmDialog from './ConfirmDialog';
import AlternatesManager from './AlternatesManager';
import { paymentsAPI } from '../../lib/alternatesApi';
import { controlCenterAPI } from '../../lib/adminControlApi';
import { tournamentAPI } from '../../lib/tournamentApi';
import { roundRobinAPI } from '../../lib/roundRobinApi';
import { adminAPI } from '../../lib/api';
import { playerCapacity, registeredPlayerCount, registeredTeamCount } from '../../lib/capacity';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';


// ---------- API family selector ---------------------------------------------

function apiFor(eventKind) {
  if (eventKind === 'tournament') {
    return {
      add:    (id, body) => tournamentAPI.addEntry(id, body),
      update: (id, eid, body) => tournamentAPI.updateEntry(id, eid, body),
      remove: (id, eid) => tournamentAPI.removeEntry(id, eid),
      replace: (id, eid, body) => tournamentAPI.replacePlayer(id, eid, body),
    };
  }
  return {
    add:    (id, body) => roundRobinAPI.addEntry(id, body),
    update: (id, eid, body) => roundRobinAPI.updateEntry(id, eid, body),
    remove: (id, eid) => roundRobinAPI.removeEntry(id, eid),
    replace: (id, eid, body) => roundRobinAPI.replacePlayer(id, eid, body),
  };
}


// ---------- Capacity Header --------------------------------------------------

function CapacityHeader({ event, eventType }) {
  const cap = playerCapacity(eventType, event.max_entries);
  const playersNow = registeredPlayerCount(event.entries || []);
  const teamsNow = eventType === 'doubles'
    ? registeredTeamCount(event.entries || [])
    : null;
  const spotsPlayers = cap == null ? null : Math.max(0, cap - playersNow);
  const spotsTeams = cap != null && eventType === 'doubles'
    ? Math.floor(spotsPlayers / 2)
    : null;

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4"
      data-testid="control-capacity-header"
    >
      {eventType === 'doubles' && (
        <div className="rounded-2xl bg-[#fbfaf8] border border-[#e3dcd1] px-3 py-3">
          <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-semibold">Teams</div>
          <div className="text-lg text-[#013523] font-semibold mt-0.5" data-testid="cap-teams">
            {teamsNow}{event.max_entries != null ? ` / ${event.max_entries}` : ''}
          </div>
        </div>
      )}
      <div className="rounded-2xl bg-[#fbfaf8] border border-[#e3dcd1] px-3 py-3">
        <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-semibold">Players</div>
        <div className="text-lg text-[#013523] font-semibold mt-0.5" data-testid="cap-players">
          {playersNow}{cap != null ? ` / ${cap}` : ''}
        </div>
      </div>
      <div className="rounded-2xl bg-[#fbfaf8] border border-[#e3dcd1] px-3 py-3 col-span-2 sm:col-span-1">
        <div className="text-[10px] uppercase tracking-wider text-[#6a766e] font-semibold">Spots Available</div>
        <div className="text-lg text-[#013523] font-semibold mt-0.5" data-testid="cap-spots">
          {cap == null
            ? 'No limit'
            : (
              <span>
                {spotsPlayers} Player{spotsPlayers === 1 ? '' : 's'}
                {eventType === 'doubles' && (
                  <span className="text-xs text-[#6a766e] font-normal">
                    {' '}· {spotsTeams} Team{spotsTeams === 1 ? '' : 's'}
                    {spotsPlayers % 2 !== 0 ? '¹' : ''}
                  </span>
                )}
              </span>
            )}
        </div>
      </div>
      {cap != null && eventType === 'doubles' && spotsPlayers % 2 !== 0 && (
        <div className="col-span-2 sm:col-span-3 text-[10.5px] text-[#6a766e]" data-testid="cap-odd-note">
          ¹ Odd player count — one incomplete team is on the roster.
        </div>
      )}
    </div>
  );
}


// ---------- Profile Picker (shared) -----------------------------------------

function ProfilePicker({ open, onClose, onPick, excludeIds = [], label, testIdPrefix = 'picker' }) {
  const [profiles, setProfiles] = useState(null);
  const [q, setQ] = useState('');

  React.useEffect(() => {
    if (!open) return;
    setProfiles(null);
    adminAPI.listApprovedProfiles()
      .then((r) => setProfiles(r.data?.profiles || []))
      .catch(() => setProfiles([]));
  }, [open]);

  const filtered = useMemo(() => {
    const all = profiles || [];
    return all.filter((p) => {
      if (excludeIds.includes(p.profile_id)) return false;
      if (!q.trim()) return true;
      const t = q.toLowerCase();
      return (p.name || '').toLowerCase().includes(t)
        || (p.email || '').toLowerCase().includes(t);
    });
  }, [profiles, excludeIds, q]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{label || 'Pick a member'}</DialogTitle>
          <DialogDescription>Search the approved-member directory and tap a name to select.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3 min-h-[280px]">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa39d]" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              data-testid={`${testIdPrefix}-search`}
              className="w-full pl-8 pr-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
            />
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-1">
            {profiles === null && <div className="text-sm text-[#6a766e] py-6 text-center">Loading…</div>}
            {profiles !== null && filtered.length === 0 && (
              <div className="text-sm text-[#6a766e] py-6 text-center" data-testid={`${testIdPrefix}-empty`}>
                No matches.
              </div>
            )}
            {filtered.map((p) => (
              <button
                key={p.profile_id}
                type="button"
                onClick={() => onPick(p)}
                data-testid={`${testIdPrefix}-item-${p.profile_id}`}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#f3f1ec] flex flex-col min-h-[44px]"
              >
                <span className="text-sm text-[#013523]">{p.name}</span>
                {p.email && <span className="text-[11px] text-[#9aa39d]">{p.email}</span>}
              </button>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Add Player Dialog -----------------------------------------------

function AddPlayerDialog({ open, onOpenChange, event, eventKind, eventType, onDone }) {
  const [step, setStep] = useState('player');
  const [p1, setP1] = useState(null);
  const [partnerMode, setPartnerMode] = useState('pending'); // 'pending' | 'looking' | 'confirmed'
  const [p2, setP2] = useState(null);
  const [busy, setBusy] = useState(false);

  const existingProfileIds = useMemo(() => {
    const ids = [];
    (event.entries || []).forEach((e) => {
      if (e.player1_profile_id) ids.push(e.player1_profile_id);
      if (e.player2_profile_id) ids.push(e.player2_profile_id);
    });
    return ids;
  }, [event.entries]);

  React.useEffect(() => {
    if (!open) {
      setStep('player'); setP1(null); setPartnerMode('pending'); setP2(null); setBusy(false);
    }
  }, [open]);

  const cap = playerCapacity(eventType, event.max_entries);
  const now = registeredPlayerCount(event.entries || []);
  const spotsLeft = cap == null ? Infinity : Math.max(0, cap - now);
  const needSlots = partnerMode === 'confirmed' ? 2 : 1;
  const eventIsFull = spotsLeft < needSlots;

  const submit = async () => {
    if (!p1) return;
    setBusy(true);
    try {
      const body = { player1_profile_id: p1.profile_id };
      if (eventType === 'doubles') {
        if (partnerMode === 'confirmed') {
          if (!p2) { setBusy(false); return; }
          body.player2_profile_id = p2.profile_id;
        } else if (partnerMode === 'looking') {
          body.partner_status = 'looking_for_partner';
        } else {
          body.partner_status = 'not_registered';
        }
      }
      await apiFor(eventKind).add(event.id, body);
      toast.success('Player added.');
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not add.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="admin-add-dialog">
        <DialogHeader>
          <DialogTitle>Add player</DialogTitle>
          <DialogDescription>
            Add an approved member to the registered roster. Composition and capacity are enforced automatically.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 min-h-[280px]">
          {eventIsFull && (
            <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="add-full-warning">
              Event is at capacity. Place the player on the Alternate List, or first create a spot by removing/separating an existing entry.
            </div>
          )}
          {/* Player 1 */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e] font-semibold mb-1">Player</div>
            {p1 ? (
              <div className="flex items-center justify-between bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-3 py-2">
                <div className="text-sm text-[#013523]">{p1.name}</div>
                <button type="button" onClick={() => setP1(null)} data-testid="add-p1-clear" className="text-[#6a766e]"><X size={14} /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setStep('pick_p1')}
                data-testid="add-p1-btn"
                className="w-full text-left px-3 py-2.5 border border-dashed border-[#c3ccc6] rounded-xl text-sm text-[#6a766e] hover:border-[#013523]/40 min-h-[44px]"
              >
                Choose a player…
              </button>
            )}
          </div>

          {/* Partner selection — doubles only */}
          {eventType === 'doubles' && p1 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6a766e] font-semibold mb-1">Partner</div>
              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {[
                  { key: 'pending',   label: 'Pending / TBD' },
                  { key: 'looking',   label: 'Looking' },
                  { key: 'confirmed', label: 'Choose partner' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { setPartnerMode(opt.key); if (opt.key !== 'confirmed') setP2(null); }}
                    data-testid={`add-partner-mode-${opt.key}`}
                    className={`text-[11.5px] px-2 py-2 rounded-lg border min-h-[40px] ${
                      partnerMode === opt.key
                        ? 'bg-[#013523] text-white border-[#013523]'
                        : 'bg-white text-[#013523] border-[#e3dcd1]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {partnerMode === 'confirmed' && (
                p2 ? (
                  <div className="flex items-center justify-between bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-3 py-2">
                    <div className="text-sm text-[#013523]">{p2.name}</div>
                    <button type="button" onClick={() => setP2(null)} data-testid="add-p2-clear" className="text-[#6a766e]"><X size={14} /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setStep('pick_p2')}
                    data-testid="add-p2-btn"
                    className="w-full text-left px-3 py-2.5 border border-dashed border-[#c3ccc6] rounded-xl text-sm text-[#6a766e] hover:border-[#013523]/40 min-h-[44px]"
                  >
                    Choose a partner…
                  </button>
                )
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="add-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !p1 || eventIsFull || (partnerMode === 'confirmed' && !p2)}
            data-testid="add-submit"
            className="bg-[#013523] text-white hover:bg-[#024029]"
          >
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>

        <ProfilePicker
          open={step === 'pick_p1'}
          onClose={() => setStep('player')}
          onPick={(p) => { setP1(p); setStep('player'); }}
          excludeIds={existingProfileIds}
          label="Choose player"
          testIdPrefix="add-p1-picker"
        />
        <ProfilePicker
          open={step === 'pick_p2'}
          onClose={() => setStep('player')}
          onPick={(p) => { setP2(p); setStep('player'); }}
          excludeIds={[...existingProfileIds, p1?.profile_id].filter(Boolean)}
          label="Choose partner"
          testIdPrefix="add-p2-picker"
        />
      </DialogContent>
    </Dialog>
  );
}


// ---------- Per-Player Row --------------------------------------------------

function PlayerRow({
  profileId, name, partnerName, partnerProfileId, partnerStatus,
  entry, isP2ofEntry, paid, paidAt, registeredAt,
  eventKind, event, onChanged,
  incompleteProfiles,  // Array of {profile_id, name} that are currently incomplete
}) {
  const [busy, setBusy] = useState(false);
  const [confirmSeparate, setConfirmSeparate] = useState(false);
  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState(false);
  const [confirmRemoveTeam, setConfirmRemoveTeam] = useState(false);
  const [confirmUnpaid, setConfirmUnpaid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assignPartnerOpen, setAssignPartnerOpen] = useState(false);
  const [pickerPartnerOpen, setPickerPartnerOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);

  // Replace Player lock: this player already has a completed/scored match in
  // this event (checked against the entry's matches). Backend enforces the
  // same rule — the frontend only mirrors it for a clear UX.
  const playerLocked = useMemo(() => (
    (event.matches || []).some((m) => (
      m.status === 'completed'
      && ((m.entry1 && m.entry1.id === entry.id) || (m.entry2 && m.entry2.id === entry.id))
    ))
  ), [event.matches, entry.id]);

  const otherProfileIds = useMemo(() => {
    const ids = [];
    (event.entries || []).forEach((e) => {
      if (e.player1_profile_id) ids.push(e.player1_profile_id);
      if (e.player2_profile_id) ids.push(e.player2_profile_id);
    });
    return ids;
  }, [event.entries]);

  async function replacePlayer(newProfile) {
    setBusy(true);
    try {
      await apiFor(eventKind).replace(event.id, entry.id, {
        old_profile_id: profileId,
        new_profile_id: newProfile.profile_id,
      });
      toast.success(`Replaced with ${newProfile.name}.`);
      setReplaceOpen(false);
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not replace player.'));
    } finally {
      setBusy(false);
    }
  }

  const existingProfileIds = useMemo(() => {
    const ids = [];
    (event.entries || []).forEach((e) => {
      if (e.player1_profile_id) ids.push(e.player1_profile_id);
      if (e.player2_profile_id) ids.push(e.player2_profile_id);
    });
    return ids;
  }, [event.entries]);

  async function togglePaid() {
    setBusy(true);
    try {
      await paymentsAPI.toggle(eventKind, event.id, profileId, !paid);
      toast.success(!paid ? 'Marked as paid.' : 'Marked as unpaid.');
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not update payment.'));
    } finally {
      setBusy(false); setConfirmUnpaid(false);
    }
  }

  async function separateTeam() {
    setBusy(true);
    try {
      await controlCenterAPI.separate(eventKind, event.id, entry.id);
      toast.success('Team separated.');
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not separate team.'));
    } finally {
      setBusy(false); setConfirmSeparate(false);
    }
  }

  // Remove just THIS player from an entry (all-or-nothing on the backend).
  // Frontend submits intent — backend re-reads state under lock and does
  // the right thing (single delete OR delete+insert-partner-as-not_registered).
  async function removePlayerFromTeam() {
    setBusy(true);
    try {
      await controlCenterAPI.removePlayer(eventKind, event.id, entry.id, profileId);
      toast.success('Player removed.');
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not remove player.'));
    } finally {
      setBusy(false); setConfirmRemovePlayer(false);
    }
  }

  async function removeTeam() {
    setBusy(true);
    try {
      await apiFor(eventKind).remove(event.id, entry.id);
      toast.success('Team removed.');
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not remove team.'));
    } finally {
      setBusy(false); setConfirmRemoveTeam(false);
    }
  }

  async function setPartnerStatus(status) {
    setBusy(true);
    try {
      await apiFor(eventKind).update(event.id, entry.id, {
        partner_status: status,
        player2_profile_id: null,
      });
      toast.success(status === 'looking_for_partner' ? 'Marked as Looking.' : 'Marked as Pending.');
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not update partner status.'));
    } finally {
      setBusy(false); setMenuOpen(false);
    }
  }

  // Assign / change partner. Backend-authoritative: submits intent only,
  // backend re-reads roster under lock and decides pair-vs-PATCH.
  async function assignPartner(partnerProfile) {
    setBusy(true);
    try {
      await controlCenterAPI.assignPartner(eventKind, event.id, profileId, partnerProfile.profile_id);
      toast.success(`Partner assigned: ${partnerProfile.name}`);
      setAssignPartnerOpen(false);
      setPickerPartnerOpen(false);
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not assign partner.'));
    } finally {
      setBusy(false);
    }
  }

  const isDoubles = event.tournament_type === 'doubles' || event.rr_type === 'doubles';
  const partnerLabel = !isDoubles ? null
    : partnerStatus === 'confirmed' && partnerName ? partnerName
    : partnerStatus === 'looking_for_partner' ? 'Looking'
    : 'Pending';
  const partnerColor = partnerStatus === 'confirmed' ? 'text-[#2a332c]'
    : partnerStatus === 'looking_for_partner' ? 'text-amber-800 italic'
    : 'text-[#9aa39d] italic';

  return (
    <li
      className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.4fr_1.2fr_auto_auto] gap-2 sm:gap-4 items-center bg-[#fbfaf8] border border-[#f3f1ec] rounded-xl px-3 py-2.5"
      data-testid={`control-player-row-${profileId}`}
    >
      <div className="min-w-0">
        <div className="text-[14.5px] text-[#013523] font-semibold truncate flex items-center gap-2">
          <MemberLink profileId={profileId} name={name} />
          {paid && (
            <span
              data-testid={`control-paid-${profileId}`}
              title={paidAt ? `Paid ${new Date(paidAt).toLocaleString()}` : 'Paid'}
              className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
            >
              <Check size={11} weight="bold" /> Paid
            </span>
          )}
        </div>
        {isDoubles && (
          <div className={`text-[12px] mt-0.5 truncate ${partnerColor} sm:hidden`} data-testid={`control-partner-mobile-${profileId}`}>
            Partner: {partnerProfileId
              ? <MemberLink profileId={partnerProfileId} name={partnerName} />
              : partnerLabel}
          </div>
        )}
        {registeredAt && (
          <div className="text-[10.5px] text-[#9aa39d] mt-0.5">
            Registered {new Date(registeredAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* Partner column — desktop */}
      {isDoubles && (
        <div className={`hidden sm:block text-[13px] truncate ${partnerColor}`} data-testid={`control-partner-desktop-${profileId}`}>
          {partnerProfileId
            ? <MemberLink profileId={partnerProfileId} name={partnerName} />
            : partnerLabel}
        </div>
      )}
      {!isDoubles && <div className="hidden sm:block" />}

      {/* Paid toggle */}
      {paid ? (
        <button
          type="button"
          onClick={() => setConfirmUnpaid(true)}
          disabled={busy}
          data-testid={`control-unmark-paid-${profileId}`}
          className="hidden sm:inline-flex text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50 items-center gap-1 min-h-[32px]"
        >
          <Check size={11} weight="bold" /> Paid
        </button>
      ) : (
        <button
          type="button"
          onClick={togglePaid}
          disabled={busy}
          data-testid={`control-mark-paid-${profileId}`}
          className="hidden sm:inline-flex text-[11px] px-2 py-1 rounded-full bg-white text-[#013523] border border-[#e3dcd1] hover:border-[#013523]/40 disabled:opacity-50 min-h-[32px]"
        >
          Mark paid
        </button>
      )}

      {/* Overflow menu */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          data-testid={`control-menu-${profileId}`}
          aria-label="More actions"
          className="w-9 h-9 rounded-full hover:bg-[#f3f1ec] flex items-center justify-center text-[#6a766e] disabled:opacity-50"
        >
          <DotsThreeVertical size={16} weight="bold" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div
              data-testid={`control-menu-open-${profileId}`}
              className="absolute right-0 top-10 z-50 w-56 bg-white border border-[#e3dcd1] rounded-xl shadow-lg py-1"
            >
              {/* Paid toggle on mobile: menu item */}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); paid ? setConfirmUnpaid(true) : togglePaid(); }}
                data-testid={`control-menu-toggle-paid-${profileId}`}
                className="sm:hidden w-full text-left text-[13px] text-[#013523] px-3 py-2 hover:bg-[#f3f1ec] min-h-[40px]"
              >
                {paid ? 'Mark as unpaid' : 'Mark as paid'}
              </button>
              {isDoubles && (
                <>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setAssignPartnerOpen(true); }}
                    data-testid={`control-menu-assign-partner-${profileId}`}
                    className="w-full text-left text-[13px] text-[#013523] px-3 py-2 hover:bg-[#f3f1ec] min-h-[40px]"
                  >
                    {partnerStatus === 'confirmed' ? 'Change partner' : 'Assign partner'}
                  </button>
                  {partnerStatus === 'confirmed' && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); setConfirmSeparate(true); }}
                      data-testid={`control-menu-separate-${profileId}`}
                      className="w-full text-left text-[13px] text-[#013523] px-3 py-2 hover:bg-[#f3f1ec] min-h-[40px]"
                    >
                      Separate team
                    </button>
                  )}
                  {partnerStatus !== 'looking_for_partner' && !isP2ofEntry && (
                    <button
                      type="button"
                      onClick={() => setPartnerStatus('looking_for_partner')}
                      data-testid={`control-menu-set-looking-${profileId}`}
                      className="w-full text-left text-[13px] text-[#013523] px-3 py-2 hover:bg-[#f3f1ec] min-h-[40px]"
                    >
                      Set to Looking
                    </button>
                  )}
                  {partnerStatus !== 'not_registered' && !isP2ofEntry && (
                    <button
                      type="button"
                      onClick={() => setPartnerStatus('not_registered')}
                      data-testid={`control-menu-set-pending-${profileId}`}
                      className="w-full text-left text-[13px] text-[#013523] px-3 py-2 hover:bg-[#f3f1ec] min-h-[40px]"
                    >
                      Set to Pending
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => { if (!playerLocked) { setMenuOpen(false); setReplaceOpen(true); } }}
                disabled={playerLocked}
                title={playerLocked ? 'This player cannot be replaced because they already have a completed match.' : undefined}
                data-testid={`control-menu-replace-player-${profileId}`}
                className={`w-full text-left text-[13px] px-3 py-2 min-h-[40px] ${playerLocked ? 'text-[#c1bcb1] cursor-not-allowed' : 'text-[#013523] hover:bg-[#f3f1ec]'}`}
              >
                Replace player{playerLocked ? ' — locked' : ''}
              </button>
              {playerLocked && (
                <div className="px-3 pb-1 text-[10.5px] text-[#9aa39d] leading-snug" data-testid={`control-replace-locked-note-${profileId}`}>
                  This player cannot be replaced because they already have a completed match.
                </div>
              )}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); setConfirmRemovePlayer(true); }}
                data-testid={`control-menu-remove-player-${profileId}`}
                className="w-full text-left text-[13px] text-red-700 px-3 py-2 hover:bg-red-50 min-h-[40px]"
              >
                Remove player
              </button>
              {isDoubles && partnerStatus === 'confirmed' && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setConfirmRemoveTeam(true); }}
                  data-testid={`control-menu-remove-team-${profileId}`}
                  className="w-full text-left text-[13px] text-red-700 px-3 py-2 hover:bg-red-50 min-h-[40px]"
                >
                  Remove entire team
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirmations */}
      <ConfirmDialog
        open={confirmUnpaid}
        onOpenChange={setConfirmUnpaid}
        title="Mark as unpaid?"
        description={`${name} will no longer show a Paid indicator on the public roster.`}
        confirmLabel="Mark unpaid"
        destructive
        onConfirm={togglePaid}
        busy={busy}
      />
      <ConfirmDialog
        open={confirmSeparate}
        onOpenChange={setConfirmSeparate}
        title="Separate team?"
        description={`Both ${name} and ${partnerName || 'partner'} stay registered as individuals (Pending). Registration timestamps and payment status are preserved.`}
        confirmLabel="Separate"
        onConfirm={separateTeam}
        busy={busy}
      />
      <ConfirmDialog
        open={confirmRemovePlayer}
        onOpenChange={setConfirmRemovePlayer}
        title="Remove player?"
        description={
          partnerStatus === 'confirmed'
            ? `Removes ${name} only. ${partnerName || 'The partner'} stays registered as Pending.`
            : `Removes ${name} from this event.`
        }
        confirmLabel="Remove player"
        destructive
        onConfirm={removePlayerFromTeam}
        busy={busy}
      />
      <ConfirmDialog
        open={confirmRemoveTeam}
        onOpenChange={setConfirmRemoveTeam}
        title="Remove entire team?"
        description={`Removes BOTH ${name} and ${partnerName || 'partner'} from this event.`}
        confirmLabel="Remove team"
        destructive
        onConfirm={removeTeam}
        busy={busy}
      />

      {/* Assign / change partner picker */}
      {assignPartnerOpen && (
        <ProfilePicker
          open={assignPartnerOpen}
          onClose={() => setAssignPartnerOpen(false)}
          onPick={assignPartner}
          excludeIds={[profileId]}
          label={partnerStatus === 'confirmed' ? 'Change partner' : 'Assign partner'}
          testIdPrefix={`control-assign-partner-${profileId}`}
        />
      )}

      {/* Replace player picker — swaps THIS player for the chosen member,
          keeping the team slot, schedule, court and paid status. */}
      {replaceOpen && (
        <ProfilePicker
          open={replaceOpen}
          onClose={() => setReplaceOpen(false)}
          onPick={replacePlayer}
          excludeIds={otherProfileIds}
          label={`Replace ${name}`}
          testIdPrefix={`control-replace-player-${profileId}`}
        />
      )}
    </li>
  );
}


// ---------- Main Control Center --------------------------------------------

export default function AdminRegistrationControlCenter({ event, eventKind, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const eventType = eventKind === 'tournament' ? event.tournament_type : event.rr_type;
  const editable = eventKind === 'tournament'
    ? ['draft', 'registration_open', 'registration_closed'].includes(event.status)
    : !['schedule_generated', 'in_progress', 'completed'].includes(event.status);

  // Per-player rows, in true individual registration order.
  const rows = useMemo(() => {
    const seen = new Map();  // profileId → row
    for (const e of event.entries || []) {
      if (e.player1_profile_id && !seen.has(e.player1_profile_id)) {
        seen.set(e.player1_profile_id, {
          profileId: e.player1_profile_id,
          name: e.player1_name,
          registeredAt: e.player1_registered_at || e.created_at || '',
          entry: e,
          isP2ofEntry: false,
          partnerStatus: e.partner_status,
          partnerName: e.partner_status === 'confirmed' ? e.player2_name : null,
          partnerProfileId: e.partner_status === 'confirmed' ? e.player2_profile_id : null,
        });
      }
      if (e.partner_status === 'confirmed' && e.player2_profile_id && !seen.has(e.player2_profile_id)) {
        seen.set(e.player2_profile_id, {
          profileId: e.player2_profile_id,
          name: e.player2_name,
          registeredAt: e.player2_registered_at || e.created_at || '',
          entry: e,
          isP2ofEntry: true,
          partnerStatus: 'confirmed',
          partnerName: e.player1_name,
          partnerProfileId: e.player1_profile_id,
        });
      }
    }
    const arr = Array.from(seen.values());
    arr.sort((a, b) => {
      const ta = String(a.registeredAt || '');
      const tb = String(b.registeredAt || '');
      if (ta !== tb) return ta < tb ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return arr;
  }, [event.entries]);

  const paidByProfile = useMemo(() => {
    const m = new Map();
    for (const p of event.payments || []) m.set(p.profile_id, p);
    return m;
  }, [event.payments]);

  const incompleteProfiles = useMemo(() => {
    const out = [];
    for (const e of event.entries || []) {
      if (e.partner_status !== 'confirmed' && e.player1_profile_id) {
        out.push({ profile_id: e.player1_profile_id, name: e.player1_name });
      }
    }
    return out;
  }, [event.entries]);

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-4" data-testid="admin-control-center">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[#013523]" />
          <div className="text-[15px] font-medium text-[#013523]">Registration Control</div>
        </div>
        {editable && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="control-add-btn"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-[#013523] text-white rounded-full px-3 py-2 hover:bg-[#024029] min-h-[40px]"
          >
            <Plus size={13} weight="bold" /> Add player
          </button>
        )}
      </div>

      <CapacityHeader event={event} eventType={eventType} />

      {!editable && (
        <div className="text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="control-locked">
          Entries are locked. Reset the bracket (or reopen registration) to edit.
        </div>
      )}

      <div className="hidden sm:grid grid-cols-[1.4fr_1.2fr_auto_auto] gap-4 pb-2 text-[10px] uppercase tracking-wider text-[#9aa39d] border-b border-[#f3f1ec]" aria-hidden>
        <div>Player</div>
        <div>{eventType === 'doubles' ? 'Partner' : ''}</div>
        <div>Paid</div>
        <div />
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-[#6a766e] py-6 text-center" data-testid="control-empty">
          No players registered yet — use “Add player” to start.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="control-list">
          {rows.map((r) => (
            <PlayerRow
              key={r.profileId}
              {...r}
              paid={!!paidByProfile.get(r.profileId)?.paid}
              paidAt={paidByProfile.get(r.profileId)?.paid_at}
              eventKind={eventKind}
              event={event}
              onChanged={onChanged}
              incompleteProfiles={incompleteProfiles}
            />
          ))}
        </ul>
      )}

      {/* Alternates — reuse the Prompt 2.2 primitive. */}
      <div className="pt-1">
        <AlternatesManager eventKind={eventKind} event={event} onChanged={onChanged} />
      </div>

      <AddPlayerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        event={event}
        eventKind={eventKind}
        eventType={eventType}
        onDone={onChanged}
      />
    </div>
  );
}
